import { describeSessionEnd, type SessionEndReason } from '@h2hspark/auth-ui';
import { Button } from '@h2hspark/ui';
import { LogIn } from 'lucide-react';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * The Sign-in required interstitial (`MEM-019` AC-3, `USR-008` AC-4).
 *
 * ## Why an interstitial and not a redirect
 *
 * `ProtectedRoute` used to send a signed-out visitor straight to `/app/login?next=…`. That
 * satisfies the second half of the AC — they return to the requested screen after signing in —
 * and not the first: the visitor never learns *what* they asked for or *why* it needed an
 * account. It matters more now than it did, because Discover is public: somebody browsing events
 * with no account who taps Connections was, until this screen, thrown into a login form with no
 * explanation of what they had touched.
 *
 * `next` is still carried, so the return half is unchanged.
 *
 * ## Why the copy names the boundary
 *
 * `MEM-AUTH-012` in one line: "discovery is open; relationship data is not". A visitor who has
 * just been browsing events freely and then meets a wall deserves to be told the rule rather than
 * left to infer that the product is arbitrary — and the rule is a promise to members rather than
 * a paywall, which is worth saying at the moment somebody first meets it.
 *
 * ## The brand's controls, since TW 44146415
 *
 * Brad reported the two actions as full-width bars with a small radius, against the pills every
 * other control in the product wears — *Sign in*, *Save changes*, *Confirm my spot*, *Send
 * connection request*. They were, because this screen was built before `Button`'s `pill-lg` size
 * and `outline-strong` variant existed and never caught up.
 *
 * **`Web App.dc.html` does not draw this screen.** So it takes the treatment of the one screen it
 * does draw near it — §3.2's sign-in, which is `handoff-corrections.md` #151's rule and the same
 * one `AuthScreen` follows: the brand's filled pill for the way forward, the 1.5px
 * `--border-default` pill on `--surface-card` for the way back, both sized to their words.
 *
 * The block is also held to `max-w-app-form` (600px, `MEM-056` AC-6's reading width for a member
 * screen that is one column) rather than running the width of the app canvas. It sits inside the
 * member shell's content area, so on a phone the column *is* the screen and nothing about it
 * changes.
 */
export function SignInRequiredPanel({
  endedBecause = null,
}: {
  /** Why the platform ended the session, when it did (`PLT-014` AC-7). Null for a plain visitor. */
  readonly endedBecause?: SessionEndReason | null;
}): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const next = `${location.pathname}${location.search}`;

  return (
    <div className="max-w-app-form space-y-5 py-6">
      <header className="space-y-1">
        <p className="font-brand text-2xs font-semibold uppercase tracking-caps text-text-subtle">
          Sign-in required
        </p>
        <h1 className="text-2xl">This part is yours alone</h1>
      </header>

      {endedBecause !== null ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {describeSessionEnd(endedBecause)}
        </p>
      ) : null}

      <p className="text-md leading-relaxed text-text-body">
        Browsing events needs no account. Who you are matched with, who you met and who you want to
        see again is yours — so it lives behind your sign-in and nowhere else.
      </p>

      {/* Wrapping rather than stacking: at 375 the two pills do not fit side by side and fall
          onto two lines, each still sized to its own words. */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="pill-lg"
          onClick={() => navigate(`/app/login?next=${encodeURIComponent(next)}`)}
        >
          <LogIn className="h-4 w-4" aria-hidden />
          Sign in
        </Button>
        <Button variant="outline-strong" size="pill-lg" onClick={() => navigate('/app')}>
          Keep browsing events
        </Button>
      </div>
    </div>
  );
}
